// Copyright 2026, Auxilio Technologies LLC

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { runsAPI, stopsAPI, DispatchRun, RouteStop } from '../services/api';
import { mapsLink, routeLink } from '../lib/orderUtils';
import { useAuthStore } from '../store/authStore';
import { Roles } from '../config/rolesConfig';
import {
  MapPinIcon,
  MapIcon,
  CheckCircleIcon,
  HomeModernIcon,
  UserGroupIcon,
  ArrowUturnLeftIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface Pending { stopId: string; outcome: string; note?: string; label: string; }

// Outcome buttons shown per stop. "Not at home" resolves to one of two real outcomes:
// Left at Door (safe place — a successful drop) or Returned to Shop (failed, comes back).
const OUTCOMES = [
  { key: 'delivered', outcome: 'delivered', icon: CheckCircleIcon, tone: 'bg-green-600 hover:bg-green-700' },
  { key: 'leftWithNeighbour', outcome: 'left_with_neighbour', icon: UserGroupIcon, tone: 'bg-blue-600 hover:bg-blue-700', needsNote: true },
  { key: 'leftAtDoor', outcome: 'safe_place', icon: HomeModernIcon, tone: 'bg-amber-600 hover:bg-amber-700' },
  { key: 'returnToShop', outcome: 'returned', icon: ArrowUturnLeftIcon, tone: 'bg-red-600 hover:bg-red-700' },
];

interface Driver { id: string; username: string; }

const DriverView: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === Roles.SUPERUSER;

  const [runs, setRuns] = useState<DispatchRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<Pending | null>(null);
  const [neighbourFor, setNeighbourFor] = useState<string | null>(null);
  const [neighbourName, setNeighbourName] = useState('');
  // Super-admin preview: which driver's view we're looking at.
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [previewDriver, setPreviewDriver] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;
  const previewRef = useRef<string>('');
  previewRef.current = previewDriver;

  const load = useCallback(() => {
    // As super admin, only load once a driver is chosen to preview.
    if (isSuperAdmin && !previewRef.current) {
      setRuns([]);
      setLoading(false);
      return;
    }
    runsAPI
      .mine(isSuperAdmin ? previewRef.current : undefined)
      .then((r) => setRuns(r.data.runs || []))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) {
      runsAPI.drivers().then((r) => setDrivers(r.data.drivers || [])).catch(() => setDrivers([]));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    load();
    // Lightweight "live" refresh (Supabase Realtime is a planned enhancement).
    const id = setInterval(() => {
      if (!pendingRef.current) load();
    }, 10000);
    return () => clearInterval(id);
  }, [load, previewDriver]);

  const commit = useCallback(
    async (p: Pending) => {
      try {
        await stopsAPI.outcome(p.stopId, p.outcome, p.note);
      } catch {
        /* ignore — reload reflects truth */
      }
      setPending(null);
      load();
    },
    [load]
  );

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingRef.current) await commit(pendingRef.current);
  }, [commit]);

  const schedule = async (stop: RouteStop, outcome: string, label: string, note?: string) => {
    await flush(); // commit any in-flight outcome first
    const p: Pending = { stopId: stop.id, outcome, note, label };
    setPending(p);
    timerRef.current = setTimeout(() => commit(p), 5000);
  };

  const undo = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setPending(null);
  };

  const onOutcome = (stop: RouteStop, o: typeof OUTCOMES[number]) => {
    if (o.needsNote) {
      setNeighbourFor(stop.id);
      setNeighbourName('');
      return;
    }
    schedule(stop, o.outcome || o.key, t(`driver.${o.key}`));
  };

  const confirmNeighbour = (stop: RouteStop) => {
    schedule(stop, 'left_with_neighbour', t('driver.leftWithNeighbour'), neighbourName || undefined);
    setNeighbourFor(null);
  };

  if (loading) return <div className="py-12 text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</div>;

  const hasStops = runs.some((r) => r.stops.length > 0);

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('driver.title')}</h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">{t('driver.subtitle')}</p>
        </div>
        <button onClick={load} className="rounded-lg p-3 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" title={t('driver.refresh')}>
          <ArrowPathIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Super-admin preview picker */}
      {isSuperAdmin && (
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-900/20">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-purple-800 dark:text-purple-300">{t('driver.previewTitle')}</p>
              <p className="text-xs text-purple-700 dark:text-purple-400">{t('driver.previewHint')}</p>
            </div>
            <select
              value={previewDriver}
              onChange={(e) => setPreviewDriver(e.target.value)}
              className="rounded-lg border border-purple-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-purple-500 dark:border-purple-700 dark:bg-gray-700 dark:text-white"
            >
              <option value="">{t('driver.previewSelect')}</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>{d.username}</option>
              ))}
            </select>
          </div>
          {previewDriver && (
            <p className="mt-2 text-xs font-medium text-purple-700 dark:text-purple-400">
              {t('driver.previewing', { name: drivers.find((d) => d.id === previewDriver)?.username || '' })}
            </p>
          )}
        </div>
      )}

      {isSuperAdmin && !previewDriver ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center dark:border-gray-600 dark:bg-gray-800">
          <TruckEmpty />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">{t('driver.previewSelect')}</h3>
        </div>
      ) : !hasStops ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center dark:border-gray-600 dark:bg-gray-800">
          <TruckEmpty />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">{t('driver.noRuns')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('driver.noRunsHint')}</p>
        </div>
      ) : (
        runs.map((run) => {
          const total = run.stops.length;
          const done = run.stops.filter((s) => s.status === 'completed' || s.status === 'failed').length;
          const deliveredN = run.stops.filter((s) => s.status === 'completed').length;
          const failedN = run.stops.filter((s) => s.status === 'failed').length;
          const allDone = total > 0 && done === total;

          const pendingAddrs = run.stops
            .filter((s) => s.status === 'pending')
            .map((s) => s.order?.delivery_address || '')
            .filter(Boolean);

          return (
            <div key={run.id} className="space-y-3">
              {allDone && (
                <div className="rounded-xl bg-green-50 p-4 text-center dark:bg-green-900/20">
                  <CheckCircleIcon className="mx-auto h-8 w-8 text-green-600" />
                  <p className="mt-1 font-semibold text-green-800 dark:text-green-300">{t('driver.complete')}</p>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    {deliveredN} {t('driver.delivshort')} · {failedN} {t('driver.failedshort')}
                  </p>
                </div>
              )}

              {/* One integrated multi-stop Google Maps route from current location */}
              {pendingAddrs.length > 0 && (
                <div>
                  <a
                    href={routeLink(pendingAddrs, run.shop_address || undefined)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 text-base font-semibold text-white shadow-sm hover:bg-rose-700"
                  >
                    <MapIcon className="h-5 w-5" /> {t('driver.startRoute', { count: pendingAddrs.length })}
                  </a>
                  <p className="mt-1 px-1 text-center text-xs text-gray-500 dark:text-gray-400">
                    {run.shop_name ? `${t('routes.source')}: ${run.shop_name}` : t('driver.routeHint')}
                  </p>
                </div>
              )}

              {run.stops.map((s, i) => {
                const isPending = pending?.stopId === s.id;
                const resolved = s.status === 'completed' || s.status === 'failed';
                const askNeighbour = neighbourFor === s.id;

                return (
                  <div
                    key={s.id}
                    className={`rounded-xl border bg-white p-4 shadow-sm dark:bg-gray-800 ${
                      resolved || isPending ? 'border-gray-200 opacity-70 dark:border-gray-700' : 'border-gray-100 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-medium uppercase text-gray-400">
                          {t('driver.stopOf', { n: i + 1, total })}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{s.order?.recipient_name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{s.order?.delivery_address}</p>
                      </div>
                      {(resolved || isPending) && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {isPending ? pending?.label : t(`order.status.${s.order?.status}`)}
                        </span>
                      )}
                    </div>

                    {s.order?.special_notes && (
                      <p className="mt-2 rounded-lg bg-rose-50 p-2 text-sm text-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                        {s.order.special_notes}
                      </p>
                    )}

                    {!resolved && !isPending && (
                      <>
                        <a
                          href={mapsLink(s.order?.delivery_address || '')}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-3 flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 text-base font-medium text-white hover:bg-rose-700"
                        >
                          <MapPinIcon className="h-5 w-5" /> {t('driver.navigate')}
                        </a>

                        {askNeighbour ? (
                          <div className="mt-3 space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('driver.neighbourPrompt')}</label>
                            <input
                              autoFocus
                              value={neighbourName}
                              onChange={(e) => setNeighbourName(e.target.value)}
                              placeholder={t('driver.neighbourPlaceholder')}
                              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            />
                            <div className="flex gap-2">
                              <button onClick={() => confirmNeighbour(s)} className="min-h-[48px] flex-1 rounded-lg bg-blue-600 px-4 font-medium text-white hover:bg-blue-700">
                                {t('driver.confirm')}
                              </button>
                              <button onClick={() => setNeighbourFor(null)} className="min-h-[48px] rounded-lg bg-gray-200 px-4 font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                                {t('common.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            {OUTCOMES.map((o) => {
                              const Icon = o.icon;
                              return (
                                <button
                                  key={o.key}
                                  onClick={() => onOutcome(s, o)}
                                  className={`flex min-h-[48px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-white ${o.tone}`}
                                >
                                  <Icon className="h-5 w-5" /> {t(`driver.${o.key}`)}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })
      )}

      {/* Undo toast */}
      {pending && (
        <div className="fixed inset-x-0 bottom-4 z-50 mx-auto flex max-w-sm items-center justify-between gap-4 rounded-xl bg-gray-900 px-4 py-3 text-white shadow-lg">
          <span className="text-sm">{t('driver.undoToast')}</span>
          <button onClick={undo} className="rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30">
            {t('driver.undo')}
          </button>
        </div>
      )}
    </div>
  );
};

const TruckEmpty: React.FC = () => (
  <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16V6a1 1 0 00-1-1H3v11h2m6 0h4m4 0h2v-5l-3-4h-3v9" />
  </svg>
);

export default DriverView;
